/**
 * Shared helpers for multi-answer question handling.
 *
 * Handles multiple data formats:
 * - Old ExamTopics scraped: "BC" (concatenated letters, A-E only)
 * - PDF/hotspot/dragdrop:   "B,G" or "A,E,H" (comma-separated, any letters)
 * - Yes/No hotspot:         "1-Yes,2-No,3-Yes" (compound row-choice keys)
 */

import type { AnswerSlot } from '../types';

const MULTI_ANSWER_TYPES = new Set(['multiple', 'hotspot', 'dragdrop']);

/** Returns true if the given question type allows multiple answers. */
export function isMultiAnswer(questionType: string): boolean {
  return MULTI_ANSWER_TYPES.has(questionType);
}

/**
 * Parse a correct_answer string into an array of individual answer keys.
 *
 * - Comma-separated compound: "1-Yes,2-No" -> ["1-Yes", "2-No"]
 * - Comma-separated simple:   "B,G"        -> ["B", "G"]
 * - Concatenated (legacy):    "BC"          -> ["B", "C"]
 * - Single letter:            "A"           -> ["A"]
 */
export function parseCorrectAnswer(answer: string): string[] {
  if (!answer) return [];

  if (answer.includes(',')) {
    return answer
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0)
      .map((s) => (s.length === 1 ? s.toUpperCase() : s));
  }

  // Legacy concatenated format (single uppercase letters only, e.g. "BC")
  if (/^[A-Za-z]+$/.test(answer) && answer.length <= 5) {
    return answer
      .split('')
      .filter((ch) => /[A-Za-z]/.test(ch))
      .map((ch) => ch.toUpperCase());
  }

  // Single value (could be "A" or a longer text like "1-Yes")
  return [answer];
}

/** Format selected answers into a string for API submission. */
export function formatAnswerForSubmission(selected: string | string[]): string {
  if (Array.isArray(selected)) {
    return selected.join(',');
  }
  return selected;
}

/** Detect if options follow the Yes/No hotspot grid pattern (letters like "1-Yes", "1-No"). */
export function isYesNoGrid(options: { letter: string }[]): boolean {
  if (options.length < 2) return false;
  return options.every((o) => /^\d+-(?:Yes|No)$/.test(o.letter));
}

/** Group Yes/No options by row number. Returns rows sorted by row number. */
export function groupYesNoRows(options: { id: number; letter: string; option_text: string }[]): {
  row: number;
  options: { id: number; letter: string; option_text: string }[];
}[] {
  const rowMap = new Map<number, { id: number; letter: string; option_text: string }[]>();
  for (const opt of options) {
    const match = opt.letter.match(/^(\d+)-(Yes|No)$/);
    if (!match) continue;
    const row = parseInt(match[1], 10);
    if (!rowMap.has(row)) rowMap.set(row, []);
    rowMap.get(row)!.push(opt);
  }
  return Array.from(rowMap.entries())
    .sort(([a], [b]) => a - b)
    .map(([row, opts]) => ({ row, options: opts }));
}

/* ── Slot-based question helpers ──────────────────────────────────────────── */

/** Check if a question should use slot-based rendering. */
export function isSlotBased(question: {
  answer_slots?: AnswerSlot[] | null;
  render_as?: string | null;
}): boolean {
  return (
    question.answer_slots != null &&
    question.answer_slots.length > 0 &&
    question.render_as != null
  );
}

/** Format slot selections into a comma-separated submission string. */
export function formatSlotAnswers(
  selections: Record<number, string>,
  slots: AnswerSlot[],
): string {
  return slots
    .sort((a, b) => a.slot_id - b.slot_id)
    .map((s) => selections[s.slot_id] ?? '')
    .join(',');
}
